/**
 * PATH: packages/email-hub/src/feature/personal/PersonalBoardsPage.tsx
 * FILE_ID: EMAILHUB-PERSONAL-BOARDSPAGE-001
 * PURPOSE: The Personal board mirror — the operator's visible boards grouped
 *          by org (sub-boards nested), each showing the cross-org emails THEY
 *          have privately pinned onto it, with a one-click un-pin.
 * WHY:     FEAT-2026-06-22-CROSS-ORG-PERSONAL — "a duplication under Personal
 *          which replicates the Board structure in sub-boards by Organisation
 *          … and a Sync Across that, not the other way around". The pins ARE
 *          that one-way sync: they overlay onto a read-only mirror of each
 *          org's board tree without writing into the org.
 * FLOW:
 *   usePersonalBoards (grouped by org, nested by parent_board) +
 *   usePersonalPins (overlay) → per-board pinned-email chips → useRevokePin
 * DEPENDENCIES:
 *   - usePersonalData: boards + pins + revoke.
 *   - PersonalSourcePill.orgHue: consistent per-org colour.
 * RELATED:
 *   - PersonalInboxPage.tsx: where pins are created.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Building2,
  ChevronLeft,
  Layers,
  Loader2,
  Mail,
  X,
} from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import { emailPath } from '../lib/basePath'
import { orgHue } from './orgColors'
import {
  usePersonalBoards,
  usePersonalPins,
  useRevokePin,
  type PersonalMirrorBoard,
  type PersonalPin,
} from './usePersonalData'

/** One board row in the mirror: its name (sub-boards indented) + the caller's
 *  pinned emails as removable chips. */
function BoardBlock({
  board,
  pins,
  depth,
  onUnpin,
  revokingId,
}: {
  board: PersonalMirrorBoard
  pins: PersonalPin[]
  depth: number
  onUnpin: (pinId: string) => void
  revokingId: string | null
}) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3"
      style={{ marginLeft: depth * 16 }}
    >
      <div className="flex items-center gap-2">
        {board.parent_board ? (
          <Layers className="h-3.5 w-3.5 text-slate-300" />
        ) : (
          <Layers className="h-4 w-4 text-slate-400" />
        )}
        <span className="text-sm font-medium text-slate-800">{board.name}</span>
        {pins.length > 0 && (
          <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
            {pins.length} pinned
          </span>
        )}
      </div>
      {pins.length > 0 && (
        <ul className="mt-2 space-y-1">
          {pins.map((pin) => (
            <li
              key={pin.id}
              className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2.5 py-1.5"
            >
              <span className="flex min-w-0 items-center gap-2">
                <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="min-w-0">
                  <span className="block truncate text-sm text-slate-700">
                    {pin.email_subject || '(no subject)'}
                  </span>
                  <span className="block truncate text-[11px] text-slate-400">
                    {pin.email_from_name || pin.email_from_address} · from{' '}
                    {pin.source_org_name}
                  </span>
                </span>
              </span>
              <button
                type="button"
                disabled={revokingId === pin.id}
                onClick={() => onUnpin(pin.id)}
                title="Remove this pin"
                className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-500 disabled:opacity-50"
              >
                {revokingId === pin.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <X className="h-3.5 w-3.5" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function PersonalBoardsPage() {
  const boardsQuery = usePersonalBoards()
  const pinsQuery = usePersonalPins()
  const revoke = useRevokePin()
  const [revokingId, setRevokingId] = useState<string | null>(null)

  const groups = boardsQuery.data ?? []
  // Memoised so the derived list is referentially stable across renders
  // (it feeds the pinsByBoard useMemo dependency array).
  const pins = useMemo(() => pinsQuery.data ?? [], [pinsQuery.data])
  const isLoading = boardsQuery.isLoading || pinsQuery.isLoading
  const isError = boardsQuery.isError

  // Pins keyed by their target board, for the per-board overlay.
  const pinsByBoard = useMemo(() => {
    const map = new Map<string, PersonalPin[]>()
    for (const pin of pins) {
      const list = map.get(pin.target_board) ?? []
      list.push(pin)
      map.set(pin.target_board, list)
    }
    return map
  }, [pins])

  const handleUnpin = async (pinId: string) => {
    if (revoke.isPending) return
    setRevokingId(pinId)
    try {
      await revoke.mutateAsync(pinId)
    } catch {
      /* mutation invalidates on success; on failure the chip stays */
    } finally {
      setRevokingId(null)
    }
  }

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3">
        <div className="flex items-center gap-2">
          <Link
            to={emailPath('/personal')}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Back to Personal inbox"
          >
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-base font-semibold text-slate-900">
              Personal boards
            </h1>
            <p className="text-xs text-slate-500">
              Your boards across every organisation - your cross-tagged emails
              overlay here, privately.
            </p>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your boards…
          </div>
        ) : isError ? (
          <div className="py-16 text-center text-sm text-slate-500">
            Could not load your boards.
          </div>
        ) : groups.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">
            No boards across your organisations yet.
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map((group) => (
              <section key={group.org_id}>
                <div
                  className={cn(
                    'mb-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset',
                    orgHue(group.org_id),
                  )}
                >
                  <Building2 className="h-3.5 w-3.5" />
                  {group.org_name}
                </div>
                <div className="space-y-2">
                  {group.boards.map((board) => (
                    <BoardBlock
                      key={board.id}
                      board={board}
                      pins={pinsByBoard.get(board.id) ?? []}
                      depth={board.parent_board ? 1 : 0}
                      onUnpin={handleUnpin}
                      revokingId={revokingId}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
